import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApprovalActionResponse,
  ApprovalInboxResponse,
  BulkApproveRequest,
  BulkApproveResponse,
  OutstandingApprovalItemDetail,
  OutstandingApprovalsFilter,
  OutstandingApprovalsResponse,
  ReportingPeriodsResponse,
  UserHistoryResponse,
} from '@time-tracking/shared';

import { useChicagoToday, useInvalidateAt } from '../hooks/useChicagoDayBoundary.js';
import { parseJson } from './client.js';
import { useApiFetch } from './useApiFetch.js';

export function useReportingPeriods(around: string) {
  const fetch = useApiFetch();
  return useQuery<ReportingPeriodsResponse>({
    queryKey: ['reporting-periods', around],
    queryFn: () =>
      fetch(`/v1/reporting-periods?around=${around}`).then((r) =>
        parseJson<ReportingPeriodsResponse>(r),
      ),
    enabled: Boolean(around),
  });
}

export function useApprovalInbox(
  weekStartDate: string | undefined,
  scope?: 'my_teams' | 'all_teams',
) {
  const fetch = useApiFetch();
  return useQuery<ApprovalInboxResponse>({
    queryKey: ['approvals', 'inbox', weekStartDate, scope ?? 'my_teams'],
    queryFn: () => {
      const qs =
        scope === 'all_teams'
          ? `weekStartDate=${weekStartDate}&scope=all`
          : `weekStartDate=${weekStartDate}`;
      return fetch(`/v1/approvals/inbox?${qs}`).then((r) => parseJson<ApprovalInboxResponse>(r));
    },
    enabled: Boolean(weekStartDate),
  });
}

export function useApprove() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    ApprovalActionResponse,
    Error,
    { timesheetId: string; weekStartDate: string | undefined; scope?: 'my_teams' | 'all_teams' },
    { snapshot: ApprovalInboxResponse | undefined }
  >({
    mutationFn: ({ timesheetId }) =>
      fetch(`/v1/timesheets/${timesheetId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
      }).then((r) => parseJson<ApprovalActionResponse>(r)),
    onMutate: async ({ timesheetId, weekStartDate, scope }) => {
      // Cancel the whole family, not just the week key: with focus-refetch left
      // on, an in-flight outstanding-list fetch that started before this mutation
      // would land after the optimistic removal and resurrect the row.
      await queryClient.cancelQueries({ queryKey: ['approvals'] });
      const snapshot = queryClient.getQueryData<ApprovalInboxResponse>([
        'approvals',
        'inbox',
        weekStartDate,
        scope ?? 'my_teams',
      ]);
      queryClient.setQueryData<ApprovalInboxResponse>(
        ['approvals', 'inbox', weekStartDate, scope ?? 'my_teams'],
        (old) =>
          old && {
            ...old,
            entries: old.entries.map((e) =>
              e.timesheetId === timesheetId ? { ...e, status: 'approved' as const } : e,
            ),
          },
      );
      return { snapshot };
    },
    onError: (_err, { weekStartDate, scope }, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(
          ['approvals', 'inbox', weekStartDate, scope ?? 'my_teams'],
          context.snapshot,
        );
      }
    },
    onSettled: () => {
      // Prefix, so the cross-week aggregate and any open drawer detail refresh
      // alongside the week they were opened from.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      // And the actor's OWN timesheet, because the sheet just acted on may be theirs:
      // R22 keeps an approver's own sheet individually approvable, and the triage view
      // puts an Approve button on that row. Without this the Timesheet page keeps
      // serving the pre-action status from cache — approved hours still reading
      // "submitted" to the person who approved them.
      //
      // The prefix rather than the one week, and unconditionally rather than only when
      // the sheet is the actor's: `['timesheet', …]` holds only the current user's own
      // weeks, so this is a handful of keys, and invalidation merely marks unmounted
      // queries stale rather than refetching them. Guessing which week or whose sheet
      // buys nothing and gets it wrong when a cross-week row is acted on from a
      // different week's inbox.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

export function useReturn() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    ApprovalActionResponse,
    Error,
    {
      timesheetId: string;
      comment: string;
      weekStartDate: string | undefined;
      scope?: 'my_teams' | 'all_teams';
    }
  >({
    mutationFn: ({ timesheetId, comment }) =>
      fetch(`/v1/timesheets/${timesheetId}/return`, {
        method: 'POST',
        body: JSON.stringify({ comment, idempotencyKey: crypto.randomUUID() }),
      }).then((r) => parseJson<ApprovalActionResponse>(r)),
    onSettled: () => {
      // Prefix, so the cross-week aggregate and any open drawer detail refresh
      // alongside the week they were opened from.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      // And the actor's OWN timesheet, because the sheet just acted on may be theirs:
      // R22 keeps an approver's own sheet individually approvable, and the triage view
      // puts an Approve button on that row. Without this the Timesheet page keeps
      // serving the pre-action status from cache — approved hours still reading
      // "submitted" to the person who approved them.
      //
      // The prefix rather than the one week, and unconditionally rather than only when
      // the sheet is the actor's: `['timesheet', …]` holds only the current user's own
      // weeks, so this is a handful of keys, and invalidation merely marks unmounted
      // queries stale rather than refetching them. Guessing which week or whose sheet
      // buys nothing and gets it wrong when a cross-week row is acted on from a
      // different week's inbox.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

export function useUnapprove() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    ApprovalActionResponse,
    Error,
    {
      timesheetId: string;
      comment: string;
      weekStartDate: string | undefined;
      scope?: 'my_teams' | 'all_teams';
    },
    { snapshot: ApprovalInboxResponse | undefined }
  >({
    mutationFn: ({ timesheetId, comment }) =>
      fetch(`/v1/timesheets/${timesheetId}/unapprove`, {
        method: 'POST',
        body: JSON.stringify({ comment, idempotencyKey: crypto.randomUUID() }),
      }).then((r) => parseJson<ApprovalActionResponse>(r)),
    onMutate: async ({ timesheetId, weekStartDate, scope }) => {
      // Cancel the whole family, not just the week key: with focus-refetch left
      // on, an in-flight outstanding-list fetch that started before this mutation
      // would land after the optimistic removal and resurrect the row.
      await queryClient.cancelQueries({ queryKey: ['approvals'] });
      const snapshot = queryClient.getQueryData<ApprovalInboxResponse>([
        'approvals',
        'inbox',
        weekStartDate,
        scope ?? 'my_teams',
      ]);
      queryClient.setQueryData<ApprovalInboxResponse>(
        ['approvals', 'inbox', weekStartDate, scope ?? 'my_teams'],
        (old) =>
          old && {
            ...old,
            entries: old.entries.map((e) =>
              e.timesheetId === timesheetId ? { ...e, status: 'returned' as const } : e,
            ),
          },
      );
      return { snapshot };
    },
    onError: (_err, { weekStartDate, scope }, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(
          ['approvals', 'inbox', weekStartDate, scope ?? 'my_teams'],
          context.snapshot,
        );
      }
    },
    onSettled: () => {
      // Prefix, so the cross-week aggregate and any open drawer detail refresh
      // alongside the week they were opened from.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      // And the actor's OWN timesheet, because the sheet just acted on may be theirs:
      // R22 keeps an approver's own sheet individually approvable, and the triage view
      // puts an Approve button on that row. Without this the Timesheet page keeps
      // serving the pre-action status from cache — approved hours still reading
      // "submitted" to the person who approved them.
      //
      // The prefix rather than the one week, and unconditionally rather than only when
      // the sheet is the actor's: `['timesheet', …]` holds only the current user's own
      // weeks, so this is a handful of keys, and invalidation merely marks unmounted
      // queries stale rather than refetching them. Guessing which week or whose sheet
      // buys nothing and gets it wrong when a cross-week row is acted on from a
      // different week's inbox.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

export function useBulkApprove() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    BulkApproveResponse,
    Error,
    { timesheetIds: string[]; weekStartDate: string | undefined; scope?: 'my_teams' | 'all_teams' }
  >({
    mutationFn: ({ timesheetIds }) => {
      const body: BulkApproveRequest = {
        timesheetIds,
        idempotencyKey: crypto.randomUUID(),
      };
      return fetch('/v1/approvals/bulk-approve', {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => parseJson<BulkApproveResponse>(r));
    },
    onSettled: () => {
      // Prefix, so the cross-week aggregate and any open drawer detail refresh
      // alongside the week they were opened from.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      // And the actor's OWN timesheet, because the sheet just acted on may be theirs:
      // R22 keeps an approver's own sheet individually approvable, and the triage view
      // puts an Approve button on that row. Without this the Timesheet page keeps
      // serving the pre-action status from cache — approved hours still reading
      // "submitted" to the person who approved them.
      //
      // The prefix rather than the one week, and unconditionally rather than only when
      // the sheet is the actor's: `['timesheet', …]` holds only the current user's own
      // weeks, so this is a handful of keys, and invalidation merely marks unmounted
      // queries stale rather than refetching them. Guessing which week or whose sheet
      // buys nothing and gets it wrong when a cross-week row is acted on from a
      // different week's inbox.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

export function useUserHistory(
  userId: string | null,
  filters?: { limit?: number; offset?: number },
) {
  const fetch = useApiFetch();
  const limit = filters?.limit ?? 20;
  const offset = filters?.offset ?? 0;
  return useQuery<UserHistoryResponse>({
    queryKey: ['user-history', userId, limit, offset],
    queryFn: () => {
      const qs = new URLSearchParams({
        userId: userId ?? '',
        limit: String(limit),
        offset: String(offset),
      });
      return fetch(`/v1/timesheets/history?${qs.toString()}`).then((r) =>
        parseJson<UserHistoryResponse>(r),
      );
    },
    enabled: userId !== null,
  });
}

/**
 * The cross-week "Needs action" triage list.
 *
 * Filter and search are query parameters rather than client-side array work: the
 * server caps the rows, so filtering here would leave a pill's count and its
 * visible rows describing different sets.
 *
 * The Chicago calendar day is part of the key. `daysPastDue` is computed
 * server-side and rotates daily, so a tab left open across midnight would
 * otherwise keep serving yesterday's day counts and urgency rails. The
 * `useOutstandingWeeks` precedent keys on the Monday; daily granularity is
 * required here because lateness changes every day, not every week.
 */
/**
 * Cache key for the triage list.
 *
 * The Chicago calendar day is part of it. `daysPastDue` is computed server-side
 * and rotates daily, so a tab left open across midnight would otherwise keep
 * serving yesterday's day counts and urgency rails. The `useOutstandingWeeks`
 * precedent keys on the Monday; daily granularity is required here because
 * lateness changes every day, not every week.
 *
 * The key alone does not make that true — it is only re-evaluated when React renders,
 * so the day is supplied by `useChicagoToday`, which re-renders on the boundary itself.
 * Without it a focused, untouched tab would keep yesterday's counts while this comment
 * claimed otherwise.
 *
 * Exported for testing: this and {@link outstandingApprovalsPath} are the two
 * parts most likely to go quietly wrong, and there is no hook-test harness in
 * this package to catch them.
 */
export function outstandingApprovalsKey(
  scope: 'my_teams' | 'all_teams',
  filter: OutstandingApprovalsFilter,
  query: string,
  today: string,
): readonly unknown[] {
  return ['approvals', 'outstanding', scope, filter, query.trim(), today];
}

/**
 * Request path for the triage list.
 *
 * Filter and search are query parameters rather than client-side array work: the
 * server caps the rows, so filtering here would leave a pill's count and its
 * visible rows describing different sets. Defaults are omitted so the common
 * case is a bare path.
 */
export function outstandingApprovalsPath(
  scope: 'my_teams' | 'all_teams',
  filter: OutstandingApprovalsFilter,
  query: string,
): string {
  const qs = new URLSearchParams();
  if (scope === 'all_teams') qs.set('scope', 'all');
  if (filter !== 'everything') qs.set('filter', filter);
  const trimmed = query.trim();
  if (trimmed) qs.set('q', trimmed);
  const suffix = qs.toString();
  return `/v1/approvals/outstanding${suffix ? `?${suffix}` : ''}`;
}

/** The cross-week "Needs action" triage list. */
export function useOutstandingApprovals(
  scope: 'my_teams' | 'all_teams',
  filter: OutstandingApprovalsFilter,
  query: string,
) {
  const fetch = useApiFetch();
  // Not `chicagoTodayIso()` called inline: that is evaluated only on render, so a tab
  // that renders nothing across midnight never rotates the key it appears in.
  const today = useChicagoToday();
  const key = outstandingApprovalsKey(scope, filter, query, today);
  const result = useQuery<OutstandingApprovalsResponse>({
    queryKey: key,
    queryFn: () =>
      fetch(outstandingApprovalsPath(scope, filter, query)).then((r) =>
        parseJson<OutstandingApprovalsResponse>(r),
      ),
    // The search text, the filter and the scope are all part of the key, so every
    // keystroke starts a NEW query — and a new query is `isPending`, which makes the
    // view swap itself for the skeleton and unmount the search box the user is typing
    // into, losing focus mid-word. Holding the previous page keeps the box mounted and
    // the list stable while the next one loads.
    placeholderData: keepPreviousData,
  });

  // The daily rotation above keeps `daysPastDue` honest; this keeps the badge honest.
  // The server stops excluding a not-yet-submitted week the moment its `dueAt` passes,
  // which is 17:00 on a weekday, not midnight — so without this an untouched page shows
  // the pre-deadline count for the rest of the evening, exactly when someone watching
  // the page is waiting for it to change.
  //
  // Read off the response rather than computed from `items`. The rows are capped at 200
  // and sorted most-overdue-first, and a not-yet-due row is the least overdue thing
  // there is — so on a backlog big enough for the cap to bind, every row this timer
  // needed had already been truncated away and it scheduled nothing at all. The count it
  // guards is uncapped; the instant that moves it has to be too.
  useInvalidateAt(result.data?.nextAwaitingActionAt ?? null, ['approvals']);

  return result;
}

/**
 * Drawer payload for one (person, week).
 *
 * Separate from the list because building it costs roughly eight queries per
 * week; a drawer is opened a handful of times per session, so that cost belongs
 * on open rather than on every row of the list.
 */
/**
 * The drawer payload for one (person, week).
 *
 * `scope` is passed through and belongs in the key. It is not decoration: the server
 * attributes a multi-team person to the alphabetically first of the teams *the caller
 * is looking through*, so a drawer fetched without the scope the list was fetched with
 * can name a different team than the row that opened it.
 */
export function useApprovalItem(
  userId: string | null,
  weekStartDate: string | null,
  scope: 'my_teams' | 'all_teams',
) {
  const fetch = useApiFetch();
  return useQuery<OutstandingApprovalItemDetail>({
    queryKey: ['approvals', 'item', userId, weekStartDate, scope],
    queryFn: () => {
      const qs = new URLSearchParams({
        userId: userId ?? '',
        weekStartDate: weekStartDate ?? '',
      });
      if (scope === 'all_teams') qs.set('scope', 'all');
      return fetch(`/v1/approvals/item?${qs.toString()}`).then((r) =>
        parseJson<OutstandingApprovalItemDetail>(r),
      );
    },
    enabled: userId !== null && weekStartDate !== null,
  });
}
